import { useState, useCallback } from 'react';
import { errorDemoApi } from '../api/errorDemoApi';
import type {
  BusinessThrowRequest,
  ValidationThrowRequest,
  AuthThrowRequest,
  ServerThrowRequest,
  ThrowResult,
  ApiError,
} from '../types/errorDemo';

/**
 * Converts an ApiError (from axios interceptor) into a ThrowResult.
 * 4xx/5xx from /throw/* endpoints are EXPECTED — surfaced as 'result', not 'error'.
 */
function toResult(err: ApiError): ThrowResult {
  return {
    status: 'result',
    data: {
      httpStatus: err.statusCode ?? 0,
      code: err.code,
      message: err.message,
      messageEn: err.messageEn,
      detail: err.detail,
      fieldErrors: err.fieldErrors,
      traceId: err.traceId,
      timestamp: err.timestamp,
    },
  };
}

// ─── Business throw ────────────────────────────────────────────────────────────

export function useBusinessThrow() {
  const [result, setResult] = useState<ThrowResult>({ status: 'idle' });

  const throwBusiness = useCallback(async (req: BusinessThrowRequest) => {
    setResult({ status: 'loading' });
    try {
      await errorDemoApi.throwBusiness(req);
      // throwBusiness always throws — if somehow 200, treat as unexpected success
      setResult({ status: 'success', message: '200 OK (예상치 못한 성공)' });
    } catch (err) {
      setResult(toResult(err as ApiError));
    }
  }, []);

  const reset = useCallback(() => setResult({ status: 'idle' }), []);

  return { result, throwBusiness, reset };
}

// ─── Validation throw ─────────────────────────────────────────────────────────

export function useValidationThrow() {
  const [result, setResult] = useState<ThrowResult>({ status: 'idle' });

  const throwValidation = useCallback(async (req: ValidationThrowRequest) => {
    setResult({ status: 'loading' });
    try {
      await errorDemoApi.throwValidation(req);
      // 200 OK possible if input is accidentally valid
      setResult({ status: 'success', message: '200 OK — 입력이 유효합니다 (의도된 오류가 발생하지 않음)' });
    } catch (err) {
      setResult(toResult(err as ApiError));
    }
  }, []);

  const reset = useCallback(() => setResult({ status: 'idle' }), []);

  return { result, throwValidation, reset };
}

// ─── Auth throw ───────────────────────────────────────────────────────────────

export function useAuthThrow() {
  const [result, setResult] = useState<ThrowResult>({ status: 'idle' });

  const throwAuth = useCallback(async (req: AuthThrowRequest) => {
    setResult({ status: 'loading' });
    try {
      await errorDemoApi.throwAuth(req);
      setResult({ status: 'success', message: '200 OK (예상치 못한 성공)' });
    } catch (err) {
      setResult(toResult(err as ApiError));
    }
  }, []);

  const reset = useCallback(() => setResult({ status: 'idle' }), []);

  return { result, throwAuth, reset };
}

// ─── Server throw ─────────────────────────────────────────────────────────────

export function useServerThrow() {
  const [result, setResult] = useState<ThrowResult>({ status: 'idle' });

  const throwServer = useCallback(async (req: ServerThrowRequest) => {
    setResult({ status: 'loading' });
    try {
      await errorDemoApi.throwServer(req);
      setResult({ status: 'success', message: '200 OK (예상치 못한 성공)' });
    } catch (err) {
      setResult(toResult(err as ApiError));
    }
  }, []);

  const reset = useCallback(() => setResult({ status: 'idle' }), []);

  return { result, throwServer, reset };
}
